import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import ErrorAlert from "../alerts/ErrorAlert";
import TrainerAssignmentDeleteDialog from "./TrainerAssignmentDeleteDialog";
import {
  formatDateLabel,
  getAssignmentLabel,
  getAssignmentRangeLabel,
  SLOT_STATUS_META,
} from "../../pages/trainer/trainerUtils";
import TrainerRegularBookingConfigurator from "./TrainerRegularBookingConfigurator";

export default function TrainerSlotDialog({ dialog }) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const {
    activeSlot,
    isMobile,
    slotError,
    isSlotMutating,
    canAssignSlots,
    canAssignForOthers,
    canManageBookedSlots,
    currentUserId,
    adminAssigneeOptions,
    isAdminAssigneeLoading,
    regularBookingConfig,
    regularBookingPreview,
    onClose,
    onOpenRegularBooking,
    onCloseRegularBooking,
    onChangeRegularBookingDuration,
    onChangeRegularBookingMonths,
    onTakeSlot,
    onReleaseSlot,
  } = dialog;
  const [selectedAdminAssigneeId, setSelectedAdminAssigneeId] = useState("");
  const canDeleteActiveSlot =
    activeSlot?.status === "mine" ||
    (canManageBookedSlots && activeSlot?.status === "occupied");
  const selectedAdminAssignee =
    adminAssigneeOptions.find(
      (assignee) => assignee.id === selectedAdminAssigneeId,
    ) || null;
  const canSubmitSlotAssignment =
    canAssignSlots &&
    (!canAssignForOthers || Boolean(selectedAdminAssigneeId));
  const adminAssignmentPayload =
    canAssignForOthers && selectedAdminAssigneeId
      ? {
          trainerId: selectedAdminAssigneeId,
          trainerName: selectedAdminAssignee?.label || "",
        }
      : {};

  useEffect(() => {
    setIsDeleteDialogOpen(false);
  }, [activeSlot?.date, activeSlot?.sessionKey, activeSlot?.siteId]);

  useEffect(() => {
    if (!canAssignForOthers || activeSlot?.status !== "open") {
      setSelectedAdminAssigneeId("");
      return;
    }

    const defaultAssigneeId =
      adminAssigneeOptions.find((option) => option.id === currentUserId)?.id ||
      adminAssigneeOptions[0]?.id ||
      "";

    setSelectedAdminAssigneeId(defaultAssigneeId);
  }, [
    activeSlot?.date,
    activeSlot?.sessionKey,
    activeSlot?.siteId,
    activeSlot?.status,
    adminAssigneeOptions,
    canAssignForOthers,
    currentUserId,
  ]);

  const handleOpenDeleteDialog = () => {
    if (isSlotMutating || !canDeleteActiveSlot) return;
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (isSlotMutating) return;
    setIsDeleteDialogOpen(false);
  };

  const handleDelete = (options = {}) => {
    setIsDeleteDialogOpen(false);
    onReleaseSlot(options);
  };

  return (
    <>
      <Dialog
        open={Boolean(activeSlot)}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px 16px 0 0", md: 3 },
            ...(isMobile && {
              position: "fixed",
              bottom: 0,
              m: 0,
              maxHeight: "85vh",
            }),
          },
        }}
        {...(isMobile && {
          TransitionProps: { style: { alignItems: "flex-end" } },
        })}
      >
        {activeSlot && (
          <>
            <Box
              sx={{
                px: 2.5,
                pt: 2,
                pb: 1.5,
                backgroundColor: SLOT_STATUS_META[activeSlot.status].bg,
                borderBottom: "1px solid",
                borderColor: SLOT_STATUS_META[activeSlot.status].border,
              }}
            >
              {isMobile && (
                <Box
                  sx={{
                    width: 36,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: "divider",
                    mx: "auto",
                    mb: 1.5,
                  }}
                />
              )}
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {activeSlot.session.title || "Training"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateLabel(activeSlot.date, {
                      weekday: "long",
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={SLOT_STATUS_META[activeSlot.status].label}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: SLOT_STATUS_META[activeSlot.status].border,
                    color: "#fff",
                  }}
                />
              </Stack>
            </Box>

            <DialogContent sx={{ px: 2.5, py: 2 }}>
              <Stack spacing={2}>
                <Stack spacing={1}>
                  <InfoRow
                    icon="mdi:clock-outline"
                    label={`${activeSlot.session.start} – ${activeSlot.session.end}`}
                    strong
                  />
                  <InfoRow
                    icon="mdi:map-marker-outline"
                    label={activeSlot.siteName}
                  />
                  {activeSlot.assignment && (
                    <InfoRow
                      icon="mdi:account-outline"
                      label={`${getAssignmentLabel(activeSlot.assignment)}${
                        activeSlot.assignmentMode === "regular"
                          ? " (regelmäßig)"
                          : " (einmalig)"
                      }`}
                    />
                  )}
                  {activeSlot.assignmentMode === "regular" && (
                    <InfoRow
                      icon="mdi:calendar-range-outline"
                      label={getAssignmentRangeLabel(activeSlot.assignment, {
                        long: true,
                      })}
                    />
                  )}
                </Stack>

                {slotError && <ErrorAlert errorMessage={slotError} />}

                {activeSlot.status === "open" && (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    Dieser Slot ist frei — übernimm ihn einmalig oder regelmäßig.
                  </Alert>
                )}
                {activeSlot.status === "open" && canAssignForOthers && (
                  <Autocomplete
                    options={adminAssigneeOptions}
                    value={selectedAdminAssignee}
                    onChange={(_, option) =>
                      setSelectedAdminAssigneeId(option?.id || "")
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option?.label || ""}
                    loading={isAdminAssigneeLoading}
                    noOptionsText="Keine Benutzer verfügbar"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Trainer / Benutzer auswählen"
                        placeholder="Benutzer suchen"
                        helperText="Nur für Admins sichtbar. Die Auswahl wird direkt diesem Slot zugewiesen."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isAdminAssigneeLoading ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Stack spacing={0.2}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {option.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.mail || "Keine E-Mail hinterlegt"}
                            {option.isAssignedToSite
                              ? " · Standort zugewiesen"
                              : option.siteCount > 0
                              ? ` · ${option.siteCount} Standorte`
                              : " · Kein Standort"}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                  />
                )}
                {activeSlot.status === "mine" && (
                  <Alert
                    severity={
                      activeSlot.assignmentMode === "regular" ? "warning" : "info"
                    }
                    sx={{ py: 0.5 }}
                  >
                    {activeSlot.assignmentMode === "regular"
                      ? "Beim Löschen kannst du wählen, ob nur dieser Termin, dieser und alle folgenden oder die gesamte Serie entfernt werden soll."
                      : "Das Löschen entfernt nur diesen einzelnen Termin."}
                  </Alert>
                )}
                {activeSlot.status === "occupied" && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    Belegt von <strong>{getAssignmentLabel(activeSlot.assignment)}</strong>
                    {canManageBookedSlots
                      ? ". Als Admin kannst du diese Buchung ebenfalls löschen."
                      : "."}
                  </Alert>
                )}

                {activeSlot.status === "open" && (
                  <Collapse
                    in={regularBookingConfig.isOpen}
                    timeout="auto"
                    unmountOnExit
                  >
                    <TrainerRegularBookingConfigurator
                      regularBookingConfig={regularBookingConfig}
                      regularBookingPreview={regularBookingPreview}
                      onChangeDuration={onChangeRegularBookingDuration}
                      onChangeMonths={onChangeRegularBookingMonths}
                    />
                  </Collapse>
                )}
              </Stack>
            </DialogContent>

            <Divider />

            <DialogActions
              sx={{
                px: 2.5,
                py: 2,
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                "& > :not(:first-of-type)": { ml: { xs: 0, sm: 1 } },
              }}
            >
              {activeSlot.status === "open" && (
                <>
                  {regularBookingConfig.isOpen ? (
                    <>
                      <Button
                        fullWidth={isMobile}
                        variant="outlined"
                        onClick={onCloseRegularBooking}
                        disabled={isSlotMutating}
                        startIcon={<Iconify icon="mdi:arrow-left" />}
                      >
                        Zurück
                      </Button>
                      <Button
                        fullWidth={isMobile}
                        variant="contained"
                        size="large"
                        onClick={() =>
                          onTakeSlot("regular", {
                            ...regularBookingPreview,
                            ...adminAssignmentPayload,
                          })
                        }
                        disabled={isSlotMutating || !canSubmitSlotAssignment}
                        startIcon={<Iconify icon="mdi:repeat" />}
                        sx={{ fontWeight: 700 }}
                      >
                        Regelmäßig buchen
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        fullWidth={isMobile}
                        variant="outlined"
                        onClick={onOpenRegularBooking}
                        disabled={isSlotMutating || !canSubmitSlotAssignment}
                        startIcon={<Iconify icon="mdi:repeat" />}
                      >
                        Regelmäßig
                      </Button>
                      <Button
                        fullWidth={isMobile}
                        variant="contained"
                        size="large"
                        onClick={() => onTakeSlot("single", adminAssignmentPayload)}
                        disabled={isSlotMutating || !canSubmitSlotAssignment}
                        startIcon={<Iconify icon="mdi:account-check-outline" />}
                        sx={{ fontWeight: 700 }}
                      >
                        Übernehmen
                      </Button>
                    </>
                  )}
                </>
              )}
              {canDeleteActiveSlot && (
                <Button
                  fullWidth={isMobile}
                  color="error"
                  variant="contained"
                  onClick={handleOpenDeleteDialog}
                  disabled={isSlotMutating}
                  startIcon={<Iconify icon="mdi:trash-can-outline" />}
                >
                  {activeSlot.assignmentMode === "regular"
                    ? "Löschoptionen"
                    : "Buchung löschen"}
                </Button>
              )}
              <Button
                fullWidth={isMobile}
                onClick={onClose}
                disabled={isSlotMutating}
              >
                Schließen
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {activeSlot && (
        <TrainerAssignmentDeleteDialog
          trainingTitle={activeSlot.session.title}
          entryDateLabel={formatDateLabel(activeSlot.date, {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
          isDeleting={isSlotMutating}
          isOpen={isDeleteDialogOpen}
          isRegular={activeSlot.assignmentMode === "regular"}
          onClose={handleCloseDeleteDialog}
          onDelete={handleDelete}
        />
      )}
    </>
  );
}

function InfoRow({ icon, label, strong = false }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Iconify icon={icon} width={18} sx={{ color: "text.secondary" }} />
      <Typography variant="body2" sx={strong ? { fontWeight: 600 } : undefined}>
        {label}
      </Typography>
    </Stack>
  );
}
